/**
 * PATH: apps/quote/src/components/builder/JobQuoteBar.tsx
 * PURPOSE: In-builder tab strip showing every quote / variation / version of
 *          the current quote's Job (Contract | Variations | Other), so an
 *          estimator can switch between siblings without leaving the builder.
 * WHY: Quotes in a job were only openable in isolation — you couldn't see the
 *      contract, its variations, or other revisions side-by-side. A fileless
 *      variation (AR record with no Excel scope) shows its revenue and an
 *      "add scope" affordance that mints an editable builder on click.
 *      (FR: quote-builder job grouping / versions / variations.)
 * FLOW: GET /jobs/<jobId>/quotes/ -> grouped chips -> click navigates to the
 *       sibling's builder, or POST .../from-sheet/ then navigates.
 */

import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Plus } from 'lucide-react'
import { quoteApi } from '@/lib/quoteApi'
import type { JobQuoteEntry } from '@/lib/quote-api-types'
import { cn, formatCurrency } from '@/lib/utils'
import { useToast } from './Toast'

const STATUS_DOT: Record<string, string> = {
  draft: 'bg-slate-400',
  pending_approval: 'bg-amber-400',
  pending_review: 'bg-amber-400',
  approved: 'bg-emerald-500',
  sent: 'bg-blue-400',
  accepted: 'bg-emerald-600',
  rejected: 'bg-rose-500',
  expired: 'bg-slate-300',
}

function statusDot(status: string): string {
  return STATUS_DOT[status] || 'bg-slate-300'
}

export interface JobQuoteBarProps {
  // Stringified or numeric Job PK — QuoteDetail exposes it as a string, the
  // job-quotes endpoint echoes it as a number; both interpolate the same way.
  jobId: number | string
  currentQuoteId: string
}

export default function JobQuoteBar({ jobId, currentQuoteId }: JobQuoteBarProps) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const toast = useToast()

  const { data, isLoading } = useQuery({
    queryKey: ['job-quotes', jobId],
    queryFn: () => quoteApi.getJobQuotes(jobId),
    enabled: !!jobId,
  })

  const addScopeMut = useMutation({
    mutationFn: (sheetId: number) => quoteApi.createBuilderQuoteFromSheet(jobId, sheetId),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['job-quotes', jobId] })
      if (res?.builder_quote_id) navigate(`/builder/${res.builder_quote_id}`)
    },
    onError: () =>
      toast.error('Could not open variation', 'Failed to create a builder for this quote.'),
  })

  const groups = useMemo(
    () => (data?.groups || []).filter((g) => g.entries.length > 0),
    [data],
  )

  // Only show the bar when the job actually has siblings — otherwise it's just
  // noise on a standalone quote.
  if (isLoading || !data || (data.total_entries ?? 0) <= 1) return null

  const onEntryClick = (e: JobQuoteEntry) => {
    if (e.builder_quote_id) {
      if (e.builder_quote_id !== currentQuoteId) navigate(`/builder/${e.builder_quote_id}`)
      return
    }
    if (e.sheet_id != null && !addScopeMut.isPending) addScopeMut.mutate(e.sheet_id)
  }

  return (
    <div className="bg-slate-50 dark:bg-slate-900/60 border-b px-3 py-1.5 overflow-x-auto">
      <div className="flex items-center gap-3 min-w-max">
        {groups.map((group) => (
          <div key={group.key} className="flex items-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 shrink-0">
              {group.label}
            </span>
            {group.entries.map((e) => {
              const active = !!e.builder_quote_id && e.builder_quote_id === currentQuoteId
              const fileless = !e.builder_quote_id
              const pending = addScopeMut.isPending && addScopeMut.variables === e.sheet_id
              return (
                <button
                  key={e.sheet_id != null ? `s${e.sheet_id}` : `b${e.builder_quote_id}`}
                  onClick={() => onEntryClick(e)}
                  title={`${e.quote_number}${e.quote_name ? ' — ' + e.quote_name : ''}${
                    fileless ? ' (no scope yet — click to add)' : ''
                  }`}
                  className={cn(
                    'group flex items-center gap-1.5 px-2 py-1 rounded-md text-xs border whitespace-nowrap transition-colors',
                    active
                      ? 'bg-blue-600 text-white border-blue-600'
                      : fileless
                        ? 'border-dashed border-slate-300 dark:border-slate-600 text-slate-500 hover:border-blue-400 hover:text-blue-600'
                        : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-blue-400',
                  )}
                >
                  <span
                    className={cn(
                      'w-1.5 h-1.5 rounded-full shrink-0',
                      active ? 'bg-white' : statusDot(e.status),
                    )}
                  />
                  <span className="font-medium max-w-[140px] truncate">
                    {e.quote_number || 'Untitled'}
                  </span>
                  {e.revision != null && e.revision > 1 && (
                    <span
                      className={cn(
                        'text-[9px] px-1 rounded',
                        active ? 'bg-blue-500' : 'bg-slate-100 dark:bg-slate-700',
                      )}
                    >
                      r{e.revision}
                    </span>
                  )}
                  <span className={cn('tabular-nums', active ? 'text-blue-100' : 'text-slate-400')}>
                    {formatCurrency(e.total_excl_gst)}
                  </span>
                  {fileless &&
                    (pending ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Plus className="w-3 h-3 opacity-60 group-hover:opacity-100" />
                    ))}
                </button>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
